import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { startRegistration } from '@simplewebauthn/browser';
import { apiRequest } from '../../utils/api';

const PasskeyManager = () => {
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchPasskeys = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiRequest('/api/auth/webauthn/passkeys');
      setPasskeys(resp.data?.passkeys || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load passkeys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPasskeys();
  }, [fetchPasskeys]);

  const handleRegister = async () => {
    setError(null);
    setSuccess(null);
    setRegistering(true);
    try {
      const optResp = await apiRequest('/api/auth/webauthn/register-options', { method: 'POST' });
      if (!optResp.data?.options) {
        setError('Failed to generate registration options');
        return;
      }

      const regResp = await startRegistration({ optionsJSON: optResp.data.options });

      const verifyResp = await apiRequest('/api/auth/webauthn/register-verify', {
        method: 'POST',
        body: JSON.stringify({ response: regResp, deviceName: deviceName.trim() || null })
      });

      if (verifyResp.data?.success) {
        setSuccess('Passkey registered successfully');
        setDeviceName('');
        setNameDialogOpen(false);
        fetchPasskeys();
      } else {
        setError(verifyResp.data?.error || 'Registration failed');
      }
    } catch (err) {
      setError(err.message || 'Passkey registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setError(null);
    setSuccess(null);
    try {
      const resp = await apiRequest(`/api/auth/webauthn/passkeys/${deleteId}`, { method: 'DELETE' });
      if (resp.data?.success) {
        setSuccess('Passkey removed');
        setDeleteId(null);
        fetchPasskeys();
      } else {
        setError(resp.data?.error || 'Failed to delete passkey');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete passkey');
    }
  };

  const formatDate = (d) => {
    if (!d) return 'Never';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <KeyIcon color="action" />
          <Typography variant="h6" component="h2">
            Passkeys & Security Keys
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Register a passkey (Face ID, Touch ID, security key, or phone) for faster, phishing-resistant
          sign-in. After enrolling, you'll be prompted to verify with a passkey at login.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {passkeys.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No passkeys registered yet.
              </Typography>
            ) : (
              <List sx={{ mb: 2 }}>
                {passkeys.map((pk) => (
                  <ListItem
                    key={pk.id}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => setDeleteId(pk.id)}>
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemIcon>
                      <KeyIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography component="span" variant="body1">
                            {pk.deviceName || 'Unnamed passkey'}
                          </Typography>
                          {pk.backedUp && (
                            <Chip label="Synced" size="small" color="success" variant="outlined" />
                          )}
                        </Box>
                      }
                      secondary={`Added ${formatDate(pk.createdAt)} · Last used ${formatDate(pk.lastUsedAt)}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNameDialogOpen(true)}
              disabled={registering}
            >
              Add Passkey
            </Button>
          </>
        )}
      </CardContent>

      <Dialog open={nameDialogOpen} onClose={() => setNameDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Name Your Passkey</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Give this passkey a label so you can identify it later (e.g. "MacBook Touch ID", "iPhone Face ID").
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Device name (optional)"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            placeholder="e.g. MacBook Touch ID"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRegister}
            disabled={registering}
            startIcon={registering ? <CircularProgress size={16} /> : null}
          >
            {registering ? 'Registering...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Passkey?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You will no longer be able to sign in with this passkey. If this is your only passkey,
            you'll need your password to log in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PasskeyManager;
